/**
 *
 * This file handles common functionality needed in both the client and service.
 *
 *
 * Get a value from an object using a string. {abc:{123:"value"}} you would do byString(object,"abc.123")
 * @param {object} currentObject
 * @param {string} field
 *
 */
export const byString = (currentObject: string | Record<string, any>, field: string) => {
	field = field.replace(/^\./, ""); // strip a leading dot
	const fieldsArray = field.split(/\.(?![^[]*])/g); // Split field names by dots, ignoring them in brackets.
	for (let i = 0; i < fieldsArray.length; ++i) {
		// Loop through and find the attribute that matches the string passed in
		const key = fieldsArray[i].replace(/[["\]]/g, ""); // convert indexes to properties (remove brackets and dub-quotes)

		if (!currentObject) return null;
		if (typeof currentObject === "string") return null; // Reached the end of the chain

		if (key in currentObject) {
			currentObject = currentObject[key];
		} else {
			return null;
		}
	}
	return currentObject;
};

// can add values to an object from a string. Must be in `.` form abc.123
export const setPath = (object: object, path: string, value: any) =>
	path.split(".").reduce((o, p) => (o[p] = path.split(".").pop() === p ? value : o[p] || {}), object);

// This handles the initial mapping for us. It will crawl through all child objects and map those too. Parent is the current location within the object(`parent.child`). Null is top level. The mapping is all flattened
export function initObject(
	object: object,
	parent: object | string | null,
	mapping: Record<string, object | string | null>
) {
	let mapLocation: string;

	if (!parent) {
		parent = null;
	}

	if (typeof object !== "object") {
		mapLocation = parent ? `${parent}.${object}` : object;
		mapping[mapLocation] = parent;
		return;
	}

	for (const n in object) {
		if (typeof object[n] === "object" && object[n] !== "undefined") {
			mapLocation = parent ? `${parent}.${n}` : n;
			mapping[mapLocation] = parent;
			initObject(object[n], mapLocation, mapping); // If we have another object, map it
		} else {
			mapLocation = parent ? `${parent}.${n}` : n;
			mapping[mapLocation] = parent;
		}
	}
}

// Will map out a field in an object. So we don't have to loop through the whole thing every time we have a change.
export const mapField = (object: object, s: string, mapping: Record<string, object | string | null>) => {
	if (mapping[s]) {
		return;
	} // If we're already mapped move on.
	s = s.replace(/\[(\w+)\]/g, ".$1"); // convert indexes to properties
	s = s.replace(/^\./, ""); // strip a leading dot
	const a = s.split(".");
	const currentLocation = s;

	if (!mapping.hasOwnProperty(currentLocation)) {
		let newString: string | null = null;
		if (a.length > 1) {
			a.pop();
			newString = a.join(".");
		}

		mapping[currentLocation] = newString;
	}

	const newObject = byString(object, currentLocation);
	if (newObject === "undefined") {
		return;
	} // If the location doesn't exist exit.
	if (typeof newObject === "object") {
		for (const key in newObject) {
			mapField(object, `${currentLocation}.${key}`, mapping); // If we need to ke
		}
	}
};

// This will remove an item from mapping and pass back an array so that we can send out notifications
const removeChildMapping = (mapping: Record<string, object | string | null>, field: string) => {
	const removals: string[] = [];
	for (const map in mapping) {
		const lookField = `${field}.`;
		if (map.includes(lookField)) {
			removals.push(map);
			delete mapping[map];
		}
	}
	return removals;
};

// To see if we're replacing an existing field/object with an object/field that would make some of the mapping obsolete.
export const checkForObjectChange = (
	object: object,
	field: string,
	mapping: Record<string, object | string | null>
) => {
	const objectReplacing = byString(object, field);
	if (objectReplacing === null) {
		return false;
	}
	if (typeof objectReplacing === "object") {
		// we're replacing an object which requires use to remap at this level.
		return removeChildMapping(mapping, field);
	}
	if (typeof objectReplacing !== "object" && typeof field === "object") {
		// we're replacing a non object with an object. Need to map out this new object
		return removeChildMapping(mapping, field);
	}
	return null;
};
